import React, { useState, useEffect, useRef, useLayoutEffect } from "react";

const RiskMatrixTable = ({ riskMatrices = [], onCellClick } = {}) => {
    const rowHeaders = [
        'Highly Unlikely (1)',
        'Unlikely (2)',
        'Likely (3)',
        'Probable (4)',
        'Most Probable (5)'
    ];

    const colHeaders = [
        'Insignificant (1)',
        'Slightly Harmful (2)',
        'Harmful (3)',
        'Very Harmful (4)',
        'Extremely Harmful (5)'
    ];

    const cells = [
        ['Very Low', 'Very Low', 'Low', 'Low', 'Medium'],
        ['Very Low', 'Low', 'Medium', 'Medium', 'High'],
        ['Low', 'Medium', 'Medium', 'High', 'High'],
        ['Low', 'Medium', 'High', 'High', 'Very High'],
        ['Medium', 'High', 'High', 'Very High', 'Very High']
    ];

    const findMatrix = (likelihood, severity) => {
        return (riskMatrices || []).find((rm) => String(rm.likelihood) === String(likelihood) && String(rm.severity) === String(severity));
    };

    return (
        <div className="overflow-x-auto md:flex md:justify-center">
            <table className="border-collapse text-xs text-center w-auto md:w-auto min-w-max">
                <thead>
                    <tr>
                        <th className="bg-gray-900 text-white border px-3 py-2">Likelihood ↓ / Severity →</th>
                        {colHeaders.map((ch, ci) => (
                            <th key={ci} className="bg-gray-900 text-white border px-3 py-2">{ch}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {cells.map((row, ri) => (
                        <tr key={ri}>
                            <td className="bg-gray-800 text-white border px-2 py-2">{rowHeaders[ri]}</td>
                            {row.map((label, ci) => {
                                const likelihood = ri + 1;
                                const severity = ci + 1;
                                const rm = findMatrix(likelihood, severity);
                                const cellClass = (() => {
                                    switch (label) {
                                        case 'Very Low': return 'bg-green-500 text-white border';
                                        case 'Low': return 'bg-green-300 border';
                                        case 'Medium': return 'bg-yellow-400 border';
                                        case 'High': return 'bg-pink-400 text-white border';
                                        case 'Very High': return 'bg-red-500 text-white border';
                                        default: return 'border';
                                    }
                                })();

                                return (
                                    <td key={ci} className={cellClass}>
                                        {rm && onCellClick ? (
                                            <button
                                                type="button"
                                                onClick={() => onCellClick(rm.id)}
                                                className="w-full h-full text-xs"
                                                title={rm && `Likelihood: ${rm.likelihood}, Severity: ${rm.severity} (${rm.riskLevel})`}
                                            >
                                                {label}
                                            </button>
                                        ) : (
                                            <span title={rm ? `Likelihood: ${rm.likelihood}, Severity: ${rm.severity} (${rm.riskLevel})` : ''}>{label}</span>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const RiskMatrixInfo = ({ riskMatrices = [], onSelectRisk } = {}) => {
    const [show, setShow] = useState(false);
    const [coords, setCoords] = useState({ left: 0, top: 0 });
    const [buttonRect, setButtonRect] = useState(null);
    const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
    const btnRef = useRef(null);
    const tooltipRef = useRef(null);
    const hideTimerRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onResize = () => setIsMobile(window.innerWidth < 768);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // After the tooltip is rendered, measure and adjust position to keep it on-screen
    useLayoutEffect(() => {
        if (!show || !buttonRect || !tooltipRef.current) return;
        const tt = tooltipRef.current;
        const ttWidth = tt.offsetWidth;
        const ttHeight = tt.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const margin = 8;

        const spaceBelow = vh - buttonRect.bottom - margin;
        const spaceAbove = buttonRect.top - margin;

        let top;
        let left;

        if (isMobile) {
            // Mobile: constrain width to viewport with side margins, position near button but keep within viewport
            left = margin;
            const maxWidth = vw - margin * 2;
            // Prefer showing below the button; if not enough space, show above; otherwise clamp
            if (ttHeight <= spaceBelow || spaceBelow >= spaceAbove) {
                top = Math.min(vh - Math.min(ttHeight, vh * 0.6) - margin, buttonRect.bottom + margin);
            } else {
                top = Math.max(margin, buttonRect.top - margin - Math.min(ttHeight, vh * 0.6));
            }
            // ensure top is within viewport
            top = Math.max(margin, Math.min(top, vh - margin));
            setCoords({ left, top, maxWidth, maxHeight: Math.floor(vh * 0.6) });
        } else {
            // Desktop: position to the right of the button by default, flip if off-screen
            left = buttonRect.right + margin;
            if (left + ttWidth + margin > vw) {
                const altLeft = buttonRect.left - margin - ttWidth;
                if (altLeft >= margin) left = altLeft;
                else left = Math.max(margin, vw - ttWidth - margin);
            }

            if (ttHeight <= spaceBelow || spaceBelow >= spaceAbove) {
                top = Math.min(vh - ttHeight - margin, buttonRect.bottom + margin);
            } else {
                top = Math.max(margin, buttonRect.top - margin - ttHeight);
            }

            setCoords({ left, top });
        }
    }, [show, isMobile, buttonRect]);

    const cancelHide = () => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const scheduleHide = (delay = 150) => {
        cancelHide();
        hideTimerRef.current = setTimeout(() => {
            hideTimerRef.current = null;
            setShow(false);
        }, delay);
    };

    const showTooltip = () => {
        cancelHide();
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        // store button rect and set initial coords; final coords are adjusted after measuring tooltip
        setButtonRect(rect);
        setCoords({ left: rect.right + 8, top: rect.bottom + 8 });
        setShow(true);
    };

    return (
        <span className="inline-block ml-2">
            <button
                ref={btnRef}
                onMouseEnter={() => showTooltip()}
                onFocus={() => showTooltip()}
                onMouseLeave={() => scheduleHide()}
                onBlur={() => scheduleHide()}
                onClick={() => setShow((s) => !s)}
                className="text-sm text-blue-600 hover:text-blue-800 bg-blue-50 rounded-full w-6 h-6 flex items-center justify-center cursor-pointer"
            >
                i
            </button>

            {show && isMobile && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShow(false)}
                    aria-hidden="true"
                >
                    <div className="absolute inset-0 bg-black opacity-30" />
                </div>
            )}

            {show && (
                <div
                    ref={tooltipRef}
                    onMouseEnter={() => {
                        cancelHide();
                        setShow(true);
                    }}
                    onMouseLeave={() => !isMobile && scheduleHide()}
                    style={
                        isMobile
                            ? { position: 'fixed', left: coords.left ?? 16, top: coords.top ?? 16, right: 16, zIndex: 9999, maxWidth: coords.maxWidth }
                            : { position: 'fixed', left: coords.left, top: coords.top, zIndex: 9999 }
                    }
                    className={
                        isMobile
                            ? 'bg-white border rounded-lg shadow-xl p-4 overflow-auto max-h-[60vh]'
                            : 'bg-white border rounded-lg shadow-xl p-4 overflow-visible max-w-[90%]'
                    }
                >
                    {isMobile && (
                        <button
                            onClick={() => setShow(false)}
                            className="absolute top-2 right-2 text-gray-600 hover:text-gray-900"
                            aria-label="Close"
                        >
                            ×
                        </button>
                    )}
                    <RiskMatrixTable
                        riskMatrices={riskMatrices}
                        onCellClick={(id) => {
                            if (onSelectRisk) onSelectRisk(id);
                            setShow(false);
                        }}
                    />
                </div>
            )}
        </span>
    );
};

export default RiskMatrixInfo;